// import { Box, useTheme } from "@mui/material";
// import React, { useMemo } from "react";
// import Htag from "../../typography/Htag";
// import { isNil } from "lodash";
// import Flx from "../../layout/Flx";

// const TitledGroup = ({
//   title,
//   children,
//   fw,
//   sx,
//   bodySx,
//   icon,
//   headerSx,
//   headerEndContent,
//   divider,
//   variant = "h3",
//   suppressPadding,
// }) => {
//   const rootStyles = useMemo(
//     () => ({
//       flexGrow: 1,
//       ...sx,
//       ...(fw && { width: "100%" }),
//       ...(suppressPadding && { padding: 0 }),
//     }),
//     [fw, sx, suppressPadding]
//   );

//   return (
//     <Box variant="outline" sx={rootStyles}>
//       <TitledGroupHeader
//         sx={headerSx}
//         divider={divider}
//         icon={icon}
//         variant={variant}
//         title={title}
//         headerEndContent={headerEndContent}
//       />
//       <Box sx={bodySx}>{children}</Box>
//     </Box>
//   );
// };

// const TitledGroupHeader = ({
//   divider,
//   sx,
//   icon,
//   headerEndContent,
//   variant,
//   title,
// }) => {
//   const theme = useTheme();

//   const headerStyles = useMemo(
//     () => ({
//       mt: 1,
//       mb: 2,
//       position: "relative",
//       color: "#656e76",
//       borderBottom: divider
//         ? `1px solid ${theme.palette.dividerLight}`
//         : "none",
//       ...sx,
//     }),
//     [divider, sx, theme.palette.dividerLight]
//   );

//   return (
//     <Box sx={headerStyles}>
//       <Flx fw jb ac>
//         <Flx ac sx={{ gap: 1 }}>
//           {icon && (
//             <Flx
//               center
//               sx={{
//                 color: "inherit",
//                 svg: {
//                   color: "inherit",
//                 },
//               }}
//             >
//               {icon}
//             </Flx>
//           )}
//           {!isNil(title) && (
//             <Htag sx={{ color: "inherit" }} variant={variant}>
//               {title}
//             </Htag>
//           )}
//         </Flx>
//         {headerEndContent}
//       </Flx>
//     </Box>
//   );
// };

// export default TitledGroup;
import { Box, useTheme } from "@mui/material";
import React, { useMemo } from "react";
import Htag from "../../typography/Htag";
import { isNil } from "lodash";
import Flx from "../../layout/Flx";
import { te } from "date-fns/locale";

/**
 * TitledGroup Component
 * @param {Object} props
 * @param {string} props.title - The title text
 * @param {React.ReactNode} props.children - Child components
 * @param {boolean} props.fw - Full width flag
 * @param {Object} props.sx - Style overrides for root
 * @param {Object} props.bodySx - Style overrides for body
 * @param {React.ReactNode} props.icon - Optional icon to display
 * @param {Object} props.headerSx - Style overrides for header
 * @param {React.ReactNode} props.headerEndContent - Optional content to display at end of header
 * @param {boolean} props.divider - Whether to show divider beneath header
 * @param {string} props.variant - Title size variant ('h2'|'h3'|'h4'), default 'h3'
 * @param {boolean} props.suppressPadding - Whether to remove default padding
 */

const paddingAmt = 2;
const TitledGroup = ({
  title,
  children,
  fw,
  sx,
  icon,
  bodySx,
  headerSx,
  headerEndContent,
  divider,
  variant = "h3",
  suppressPadding,
  fontWeight = 600,
  // fontWeight = 500,
  suppressHeaderPadding,
  uppercase,
  headerContent,
  suppressBodyPadding,
  headerBottomPadding,
  onHeaderClick,
  spt,
  spb,
  spl,
  spr,
}) => {
  const cardRootStyles = useMemo(
    () => ({
      flexGrow: 1,

      ...sx,
      ...(fw && { width: "100%" }),
      ...(suppressPadding && { padding: 0 }),
    }),
    [fw, sx, suppressPadding]
  );

  return (
    <Box sx={cardRootStyles} className="titled-group-root">
      <TitledGroupHeader
        sx={headerSx}
        divider={divider}
        icon={icon}
        headerContent={headerContent}
        variant={variant}
        uppercase={uppercase}
        title={title}
        fontWeight={fontWeight}
        suppressHeaderPadding={suppressHeaderPadding}
        headerEndContent={headerEndContent}
        onHeaderClick={onHeaderClick}
        headerBottomPadding={headerBottomPadding}
      />

      <BodyContent
        suppressBodyPadding={suppressBodyPadding}
        sx={bodySx}
        spt={spt}
        spb={spb}
        spl={spl}
        spr={spr}
      >
        {children}
      </BodyContent>
    </Box>
  );
};

/**
 * Header component for TitledGroup
 */
const TitledGroupHeader = ({
  divider,
  sx,
  icon,
  headerEndContent,
  variant,
  title,
  fontWeight,
  headerContent,
  suppressHeaderPadding,
  headerBottomPadding,
  uppercase,
  onHeaderClick,
}) => {
  if (isNil(title) && isNil(headerEndContent)) return null;
  return (
    <TitledGroupHeaderRoot
      headerEndContent={headerEndContent}
      sx={sx}
      onHeaderClick={onHeaderClick}
      divider={divider}
      suppressHeaderPadding={suppressHeaderPadding}
      headerBottomPadding={headerBottomPadding}
    >
      <Flx ac sx={{ display: "flex", gap: 0.8 }}>
        {icon && <Icon>{icon}</Icon>}
        <Title fontWeight={fontWeight} uppercase={uppercase} variant={variant}>
          {title}
        </Title>
      </Flx>
    </TitledGroupHeaderRoot>
  );
};

/**
 * Root container for TitledGroupHeader
 */
const TitledGroupHeaderRoot = ({
  sx,
  divider,
  children,
  headerEndContent,
  suppressHeaderPadding,
  headerBottomPadding = 0,
  onHeaderClick,
}) => {
  const theme = useTheme();

  const styles = useMemo(
    () => ({
      // mt: 1,
      // pt: 1,
      // pb: 2,
      px: suppressHeaderPadding ? 0 : paddingAmt,
      pt: suppressHeaderPadding ? 0 : paddingAmt + 0.5,
      // pt: suppressHeaderPadding ? 0 : paddingAmt + 1,
      pb: headerBottomPadding,
      // mb: 2,
      position: "relative",
      // color: "#656e76",
      borderBottom: divider
        ? `1px solid ${theme.palette.dividerLight}`
        : "none",
      ...sx,
    }),
    [
      sx,
      divider,
      theme.palette.dividerLight,
      suppressHeaderPadding,
      headerBottomPadding,
    ]
  );

  return (
    <Box
      sx={styles}
      className="titled-group-header-root"
      onClick={onHeaderClick}
    >
      {headerEndContent ? (
        <Flx fw jb ac>
          {children}
          {headerEndContent}
        </Flx>
      ) : (
        children
      )}
    </Box>
  );
};

/**
 * Icon container component
 */
const Icon = ({ children }) => {
  if (isNil(children)) return null;

  return (
    <Flx
      center
      sx={{
        color: "inherit",
        svg: {
          color: "inherit",
        },
      }}
    >
      {children}
    </Flx>
  );
};

/**
 * Title component
 */
const Title = ({ children, variant, uppercase, fontWeight }) => {
  if (isNil(children)) return null;

  return (
    <Htag
      sx={{
        color: "inherit",
        textTransform: uppercase ? "uppercase" : "capitalize",

        margin: 0,
        fontWeight: fontWeight,
      }}
      variant={variant}
    >
      {children}
    </Htag>
  );
};

const BodyContent = ({
  suppressBodyPadding,
  sx,
  children,
  spt,
  spb,
  spl,
  spr,
}) => {
  const styles = useMemo(() => {
    const p = suppressBodyPadding ? 0 : paddingAmt;
    return {
      position: "relative",
      pt: spt ? 0 : p,
      pb: spb ? 0 : p,
      pl: spl ? 0 : p,
      pr: spr ? 0 : p,
      ...sx,
    };
  }, [sx, suppressBodyPadding, spt, spb, spl, spr]);

  if (isNil(children)) return null;
  return (
    <Box className="titled-group-body-root" sx={styles}>
      {children}
    </Box>
  );
};

export default TitledGroup;
